import { RoleGate } from "@/common/components/layout/RoleGate";
import { ApiError } from "@/common/lib/api";
import { useCurrentAccount } from "@/features/accounts/hooks/useCurrentAccount";
import { ChevronLeft } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { useAnimal } from "../api/animalsApi";
import { AnimalAccumulatedCostSection } from "../components/AnimalAccumulatedCostSection";
import { AnimalActionsSidebar } from "../components/AnimalActionsSidebar";
import { AnimalDetailHeader } from "../components/AnimalDetailHeader";
import {
  AnimalAccountingSection,
  AnimalInfoSections,
  AnimalLocationSection,
} from "../components/AnimalInfoSections";
import { AnimalWeightSection } from "../components/AnimalWeightSection";
import { AnimalNotFoundPage } from "./AnimalNotFoundPage";

export function AnimalDetailPage() {
  const { animalId = "" } = useParams<{ animalId: string }>();
  const { currentAccount } = useCurrentAccount();
  const accountId = currentAccount?.id ?? "";
  const { data: animal, isLoading, isError, error, refetch } = useAnimal(accountId, animalId);

  if (isLoading) {
    return (
      <>
        <title>Cargando · HATO</title>
        <Breadcrumb tag={null} />
        <DetailSkeleton />
      </>
    );
  }

  if (isError) {
    if (error instanceof ApiError && error.status === 404) {
      return <AnimalNotFoundPage />;
    }
    return (
      <>
        <title>Hato · HATO</title>
        <Breadcrumb tag={null} />
        <div className="mt-12 flex flex-col items-center gap-4 text-center">
          <p className="text-app-ink-2 text-sm">No se pudo cargar el animal.</p>
          <button
            type="button"
            onClick={() => void refetch()}
            className="rounded-xl border border-app-border bg-app-surface px-4 py-2 font-medium text-[13px] text-app-ink-1 hover:bg-app-surface-2"
          >
            Reintentar
          </button>
        </div>
      </>
    );
  }

  if (!animal) return <AnimalNotFoundPage />;

  return (
    <>
      <title>{`${animal.tag} · Hato · HATO`}</title>
      <Breadcrumb tag={animal.tag} />
      <div className="mt-4">
        <AnimalDetailHeader animal={animal} />
        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-2">
            <AnimalInfoSections animal={animal} />
            <AnimalWeightSection accountId={accountId} animal={animal} />
            <RoleGate roles={["owner", "admin", "viewer"]}>
              <AnimalAccountingSection animal={animal} />
            </RoleGate>
            <RoleGate roles={["owner", "admin", "viewer"]}>
              <AnimalAccumulatedCostSection accountId={accountId} animalId={animal.id} />
            </RoleGate>
          </div>
          <aside className="space-y-6 lg:sticky lg:top-6 lg:self-start">
            <AnimalLocationSection animal={animal} />
            <AnimalActionsSidebar accountId={accountId} animal={animal} />
          </aside>
        </div>
      </div>
    </>
  );
}

function Breadcrumb({ tag }: { tag: string | null }) {
  return (
    <nav aria-label="Migas de pan" className="flex items-center gap-2 text-[12px] text-app-ink-3">
      <Link to="/app/hato" className="inline-flex items-center gap-1 hover:text-app-ink-1">
        <ChevronLeft className="h-3.5 w-3.5" aria-hidden />
        <span>Hato</span>
      </Link>
      {tag && (
        <>
          <span aria-hidden>›</span>
          <span className="font-mono text-app-ink-2">{tag}</span>
        </>
      )}
    </nav>
  );
}

function DetailSkeleton() {
  return (
    <div className="mt-4 space-y-4">
      <div className="h-10 w-48 animate-pulse rounded-xl bg-app-surface-2" />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <div className="h-32 animate-pulse rounded-2xl bg-app-surface-2" />
          <div className="h-32 animate-pulse rounded-2xl bg-app-surface-2" />
          <div className="h-32 animate-pulse rounded-2xl bg-app-surface-2" />
        </div>
        <div className="space-y-4">
          <div className="h-24 animate-pulse rounded-2xl bg-app-surface-2" />
          <div className="h-48 animate-pulse rounded-2xl bg-app-surface-2" />
        </div>
      </div>
    </div>
  );
}
